import { Boxes, Building2, Cable, House, KeyRound, Mail, Network, Receipt, Users } from '@bloo-team/ui/icons'
import type { IconComponent } from '@bloo-team/ui/icons'
import type { NavEntry, ProductManifest, SettingsSection } from './manifest'
import type { Identity, ProductTier, RoleRequirement } from './permissions'
import { SETTINGS_GATES } from './permissions'

/**
 * Core navigation vocabulary. Since the manifest refactor
 * (docs/modularization-requirements.md, docs/product-manifest.md) this module
 * owns only what is NOT a product's to own:
 *
 *   - the shared shapes (`NavItem`, `NavGroup`, `SettingsGroup`) — app-typed
 *     extensions of the `@bloo-team/core-shell` contract types, so a manifest
 *     written here already satisfies the platform contract;
 *   - the Home rail group (the shared cross-product surface, IA4/IA8);
 *   - the platform Settings groups — Access, Tenancy, Platform, Billing;
 *   - `navPath` and the legacy-redirect table.
 *
 * Product rail groups, product settings sections and product palette actions
 * live in each product's manifest (`src/products/<id>/manifest.ts`) and are
 * composed by the registry (`lib/products.ts`). The old static `NAV_GROUPS` /
 * `SETTINGS_GROUPS` exports are gone on purpose — the registry is the only
 * composition point.
 */

/**
 * Products sharing this console. SynthAI joined 2026-08-27 once its backend
 * contract existed (docs/IA.md D-SYN lifted; docs/synthai-requirements.md).
 */
export type ProductKey = 'fabric' | 'vantage' | 'synthai'

/**
 * A nav destination — the app-typed shape of the core-shell `NavEntry`
 * contract: same structure, narrowed to this console's icon library, role
 * union and product keys.
 */
export interface NavItem extends NavEntry<Identity> {
  icon: IconComponent
  roles?: RoleRequirement
  tiers?: ProductTier[]
  /** For gates the role/tier pair cannot express — see canSee's `gate`. */
  gate?: (id: Identity) => boolean
  /**
   * Settings only: sub-sections of this entry, each its own route. A parent
   * with children is a disclosure, not a destination.
   */
  children?: NavItem[]
  /**
   * Names another product this entry needs (`integratesWith` mechanics): the
   * entry is listed only while both products are registered AND entitled.
   */
  integration?: ProductKey
}

export interface NavGroup {
  key: 'home' | ProductKey
  /** Rail header. Products use their brand names (IA16). */
  title: string | null
  /** Which entitlement gates this group; null = always available. */
  product: ProductKey | null
  items: NavItem[]
}

/** Role lists shared by the manifests (legacy role model, docs/IA.md). */
export const ANALYST_OR_ENGINEER: RoleRequirement = ['Security Engineer', 'Security Analyst']
export const INCLUDING_VIEWER: RoleRequirement = ['Security Engineer', 'Security Analyst', 'Viewer']

/** A product settings contribution, app-typed (see core-shell `SettingsSection`). */
export interface ConsoleSettingsSection extends SettingsSection<Identity> {
  items: NavItem[]
}

/**
 * A product manifest as this console writes one: the core-shell contract with
 * every open field narrowed to app vocabulary. Because `NavItem` extends
 * `NavEntry<Identity>`, a `ConsoleProductManifest` IS a `ProductManifest` —
 * the registry and, later, the extracted packages consume it unchanged.
 */
export interface ConsoleProductManifest extends ProductManifest<Identity> {
  id: ProductKey
  icon: IconComponent
  nav: NavItem[]
  settings: ConsoleSettingsSection[]
  requires?: ProductKey[]
  integratesWith?: ProductKey[]
}

/**
 * Home — the shared cross-product surface (IA4, IA8). Core-owned: it exists
 * whatever the product mix. Settings is NOT a rail group — the platform layer
 * hangs off the gear at the rail's bottom (IA13).
 */
export const HOME_GROUP: NavGroup = {
  key: 'home',
  title: null,
  product: null,
  items: [
    { key: 'home', title: 'Home', icon: House, to: '/home', roles: INCLUDING_VIEWER },
  ],
}

export interface SettingsGroup {
  key: string
  /** Section label above the group. null = ungrouped, rendered flush at the top. */
  title: string | null
  items: NavItem[]
}

/**
 * Platform Settings groups (gear menu / settings left-nav), core-owned.
 *
 * Grouped, because nine flat links do not say which of them are the same kind
 * of decision. The groups are the question being answered: who gets in
 * (Access), whose data this is (Tenancy), what the products do (the
 * manifest-contributed sections composed between LEADING and TRAILING — see
 * `settingsGroupsFor` in lib/products.ts), what the deployment runs on
 * (Platform), what it costs (Billing).
 *
 * Gates come from the legacy nav's role lists (docs/settings-requirements.md
 * S4) and are per LEAF: an item the caller cannot reach is not listed, and a
 * group whose every item is gated away does not render its label either.
 */
export const CORE_SETTINGS_GROUPS_LEADING: SettingsGroup[] = [
  {
    key: 'access',
    title: 'Access',
    items: [
      { key: 'settings-users', title: 'Users', icon: Users, to: '/settings/users', gate: SETTINGS_GATES.users },
      { key: 'settings-tokens', title: 'Tokens', icon: KeyRound, to: '/settings/tokens', gate: SETTINGS_GATES.operational },
    ],
  },
  {
    key: 'tenancy',
    title: 'Tenancy',
    items: [
      { key: 'settings-orgs', title: 'Organizations', icon: Building2, to: '/settings/organizations', gate: SETTINGS_GATES.orgs },
      { key: 'settings-tenants', title: 'Tenants', icon: Network, to: '/settings/tenants', gate: SETTINGS_GATES.tenants },
    ],
  },
]

/** Platform + Billing render after the product sections. */
export const CORE_SETTINGS_GROUPS_TRAILING: SettingsGroup[] = [
  {
    key: 'platform',
    title: 'Platform',
    items: [
      { key: 'settings-integrations', title: 'Integrations', icon: Cable, to: '/settings/integrations', gate: SETTINGS_GATES.operational },
      { key: 'settings-components', title: 'Components', icon: Boxes, to: '/settings/components', gate: SETTINGS_GATES.operational, badge: 'Soon' },
      { key: 'settings-smtp', title: 'SMTP', icon: Mail, to: '/settings/smtp', gate: SETTINGS_GATES.root, badge: 'Soon' },
    ],
  },
  {
    key: 'billing',
    title: 'Billing',
    items: [
      { key: 'settings-usage', title: 'Usage & Licensing', icon: Receipt, to: '/settings/usage', gate: SETTINGS_GATES.tenants, badge: 'Soon' },
    ],
  },
]

export function navPath(clusterId: string, scopeId: string, to: string): string {
  return `/${clusterId}/${scopeId}${to}`
}

/**
 * Legacy-path redirects (docs/IA.md mapping table). Keys are the legacy suffix
 * after /{clusterId}/{scopeId}; values are the new suffix. Served as permanent
 * redirects so six years of bookmarks keep working.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  '/dashboard': '/home',
  '/reports': '/home',
  '/workbook/new': '/fabric/search',
  // The workbook LIST is the detection library; the EDITOR (/workbook/:id,
  // prefix-matched in the catch-all) opens /vantage/detections/workbook —
  // library and editor are one section since 2026-08-22, and Hunts is the
  // ad-hoc hunting workspace (docs/hunts-requirements.md).
  '/workbook': '/vantage/detections',
  '/incident/signals': '/vantage/signals',
  '/incident/cases': '/vantage/cases',
  '/mitre_attack': '/vantage/coverage',
  '/eba': '/vantage/detections',
  '/stream/list': '/fabric/streams',
  '/system/collection_status': '/fabric/streams',
  '/system/eventstores': '/fabric/storage?tab=stores',
  '/system/extraction': '/fabric/extractors',
  '/system/enrichment': '/fabric/enrichment',
  '/system/geodb': '/fabric/enrichment',
  '/system/connectors': '/fabric/sources',
  // The stream-scoped legacy form (/system/streamfieldaddition/:stream) is
  // prefix-matched in `legacyRedirectFor` and lands on `?stream=`.
  '/system/streamfieldaddition': '/fabric/data-model',
  '/system/models': '/vantage/detections',
  '/system/signalsuppression': '/vantage/detections?tab=suppression',
  '/system/integrations': '/settings/integrations',
  '/system/utilization': '/settings/usage',
  '/system/token': '/settings/tokens',
  '/system/components': '/settings/components',
}

/**
 * Route paths this build does not ship (R6, docs/page-gating.md).
 *
 * `LEGACY_REDIRECTS` is core-owned, so no build-profile switch touches it: on a
 * build that holds back Enrichment, `/system/enrichment` still redirects to
 * `/fabric/enrichment`, a route that no longer exists. The composer therefore
 * hands core-shell the excluded paths as DATA (rule 11 — core never imports a
 * profile) and redirect resolution skips them, so a legacy bookmark lands on
 * the catch-all's "not part of this deployment" guard instead of bouncing
 * through a redirect to reach it.
 *
 * Empty on a full build, which is every build that does not set `BLOO_PROFILE`.
 */
let excludedRoutes: readonly string[] = []

/** Install the excluded route paths. Called by the generated composer only. */
export function installExcludedRoutes(paths: readonly string[]): void {
  excludedRoutes = [...paths]
}

/** The installed excluded paths — for tests and the composer's own assertions. */
export function excludedRoutePaths(): readonly string[] {
  return excludedRoutes
}

/**
 * Whether `path` names an excluded route. A page's child routes go with it
 * (`/vantage/detections` excluded also strips `/vantage/detections/workbook`,
 * exactly as `routeFileIgnorePattern`'s `^<id>[._]` strips the shim), so a
 * descendant counts as excluded too.
 */
export function isExcludedRoute(path: string): boolean {
  const clean = path.split('?')[0] ?? path
  return excludedRoutes.some((ex) => clean === ex || clean.startsWith(`${ex}/`))
}

/**
 * Resolve a legacy suffix to its new suffix (+ optional `?search`), or
 * undefined when it is not a legacy path. Exact rows of `LEGACY_REDIRECTS`
 * first, then the two prefix forms that carry an id in the path:
 *
 *   /workbook/:id                       → /vantage/detections/workbook?wb=:id
 *   /system/streamfieldaddition/:stream → /fabric/data-model?stream=:stream
 *
 * A target this build excluded resolves to undefined — see
 * {@link installExcludedRoutes}.
 *
 * The scope catch-all (`routes/$clusterId/$scopeId/$.tsx`) splits the result
 * on `?` and redirects; keeping the prefix rules here means the table and
 * its exceptions are one module (docs/data-model-requirements.md §Redirects).
 */
export function legacyRedirectFor(path: string): string | undefined {
  const target = resolveLegacy(path)
  if (target === undefined || isExcludedRoute(target)) return undefined
  return target
}

function resolveLegacy(path: string): string | undefined {
  const exact = LEGACY_REDIRECTS[path]
  if (exact) return exact
  const tail = (prefix: string) => path.slice(prefix.length).split('/')[0] ?? ''
  if (path.startsWith('/workbook/')) {
    return `/vantage/detections/workbook?wb=${encodeURIComponent(tail('/workbook/'))}`
  }
  if (path.startsWith('/system/streamfieldaddition/')) {
    const stream = tail('/system/streamfieldaddition/')
    return stream ? `/fabric/data-model?stream=${encodeURIComponent(stream)}` : '/fabric/data-model'
  }
  return undefined
}
